import React, { useEffect, useState } from 'react'
import {
  IonButton,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemGroup,
  IonLabel,
  IonList,
  IonLoading,
  IonRow,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { getEKGImportSelector } from '../selectors'
import { useTranslation } from 'react-i18next'
import '../page/EKGImportMain.page.css'
import './ImportSummary.page.css'
import { chevronBack } from 'ionicons/icons'
import { getEventsAndReportsResults, saveEkgImportData } from '../store/ekgImport.actions'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import EventItem from '../eventItem/EventItem.component'
import ReportItem from '../reportItem/ReportItem.component'
import Logger from '../../../services/logger.service'
import { getVitalOptionsSelector } from '../../options/selectors'

interface IImportSummaryProps {
  onPrevious: () => void
  onCancel: () => void
}

const ImportSummary = (props: IImportSummaryProps) => {
  const { onPrevious, onCancel } = props

  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const { selectedEvents, selectedReports, selectedPowerOnTime, isLoading } = useSelector(getEKGImportSelector)
  const { temperatureMethodOptions } = useSelector(getVitalOptionsSelector)
  const [isSaving, setIsSaving] = useState(false)

  const onMount = () => {
    Logger.info('ECGImport: The user is on the import summary / confirmation screen')
    dispatch(getEventsAndReportsResults())
  }

  useEffect(() => {
    onMount()
  }, [])

  const logSubmit = () => {
    const numberOfEvents = selectedEvents.length
    const numberOfReports = selectedReports.length

    Logger.info(
      `ECGImport: The user has imported ${numberOfEvents} events and ${numberOfReports} reports from ECG ${selectedPowerOnTime?.id} on service ${selectedPowerOnTime?.serviceId}`
    )
  }

  const onSubmit = async () => {
    if (isSaving) return

    setIsSaving(true)
    setTimeout(() => {
      dispatch(saveEkgImportData())
      logSubmit()

      history.push(TabConstants.TIMELINE_TAB)
    }, 10)
  }

  const renderFooter = () => {
    return (
      <IonFooter className='ekg-footer'>
        <IonButton className='ekg-footer-button' onClick={onCancel}>
          {t('common.buttons.Cancel')}
        </IonButton>
        <div className='ekg-footer-button-group'>
          <IonButton className='ekg-footer-button' onClick={onPrevious}>
            <IonIcon className='ekg-footer-button-chevron' icon={chevronBack} color={'#6fa5d6'} />
            {t('common.buttons.Back')}
          </IonButton>
          <IonButton className='ekg-footer-button-alt' onClick={onSubmit}>
            {isSaving ? <>{t('common.buttons.Importing')}</> : <>{t('common.buttons.Import')}</>}
          </IonButton>
        </div>
      </IonFooter>
    )
  }

  const renderGroupHeader = (title: 'events' | 'reports') => {
    return (
      <IonItemGroup className='section-banner'>
        <IonGrid className='section-banner-grid'>{t(`ekg.${title}`)}</IonGrid>
      </IonItemGroup>
    )
  }

  const renderNoContent = (title: 'events' | 'reports') => {
    const message = title === 'events' ? 'No Events Selected' : 'No Reports Selected'

    return <div className='no-content'>{message}</div>
  }

  const renderEventsTableHeader = () => {
    return (
      <>
        <IonItem className='event-header-item'>
          <IonGrid className='event-item-grid-summary'>
            <IonRow>
              <IonCol className='event-time'>
                <IonLabel>
                  <h3>{t('ekg.time')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-description'>
                <IonLabel>
                  <h3>{t('ekg.description')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-heartRate'>
                <IonLabel>
                  <h3>{t('ekg.hr')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-systolicBP'>
                <IonLabel>
                  <h3>{t('ekg.SBP')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-diastolicBP'>
                <IonLabel>
                  <h3>{t('ekg.DBP')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-map'>
                <IonLabel>
                  <h3>{t('ekg.MAP')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-spo2'>
                <IonLabel>
                  <h3>{t('ekg.SpO2')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-etco2'>
                <IonLabel>
                  <h3>{t('ekg.EtCo2')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-resp'>
                <IonLabel>
                  <h3>{t('ekg.Resp')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-spco'>
                <IonLabel>
                  <h3>{t('ekg.SpCo')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-spmet'>
                <IonLabel>
                  <h3>{t('ekg.SpMet')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-temp'>
                <IonLabel>
                  <h3>{t('ekg.Temp')}</h3>
                </IonLabel>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonItem>
        <div className='summary-header-hr' />
      </>
    )
  }

  const renderReportsTableHeader = () => {
    return (
      <>
        <IonItem className='event-header-item'>
          <IonGrid className='event-item-grid-summary'>
            <IonRow>
              <IonCol className='event-description-report'>
                <IonLabel>
                  <h3>{t('ekg.name')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-size'>
                <IonLabel>
                  <h3>{t('ekg.size')}</h3>
                </IonLabel>
              </IonCol>
              <IonCol className='event-more'></IonCol>
            </IonRow>
          </IonGrid>
        </IonItem>
        <div className='summary-header-hr' />
      </>
    )
  }

  const renderLoader = () => {
    return <IonLoading isOpen animated showBackdrop spinner={'crescent'} />
  }

  return (
    <>
      <IonContent fullscreen>
        <IonList lines='full'>
          {renderGroupHeader('events')}
          {!selectedEvents.length && !isLoading && renderNoContent('events')}
          {!!selectedEvents.length && !isLoading && renderEventsTableHeader()}
          {!!selectedEvents.length && !isLoading && (
            <div className='summary-section-list'>
              {selectedEvents.map((event, i) => {
                return <EventItem key={i} event={event} temperatureMethodOptions={temperatureMethodOptions} />
              })}
            </div>
          )}
          {renderGroupHeader('reports')}
          {!selectedReports.length && !isLoading && renderNoContent('reports')}
          {!!selectedReports.length && !isLoading && renderReportsTableHeader()}
          {!!selectedReports.length && !isLoading && (
            <div className='summary-section-list'>
              {selectedReports.map((report, i) => {
                return <ReportItem key={i} report={report} />
              })}
            </div>
          )}
        </IonList>
        {isSaving && renderLoader()}
      </IonContent>
      {renderFooter()}
    </>
  )
}

export default ImportSummary
